import { getUsers, isDiscordIsVerified } from "../../utils/getUser";
import { OpenOrdersLogo } from "../../assests/logo/openOrdersLogo";
import { QuotesLogo } from "../../assests/logo/quotesLogo";
import { PreQuotedLogo } from "../../assests/logo/preQuotedLogo";
import { ShippingLogo } from "../../assests/logo/shippingLogo";
import { PayoutLogo } from "../../assests/logo/payoutsLogo";
import { HelpLogo } from "../../assests/logo/helpLogo";
import { InboundLogo } from "../../assests/logo/inboundLogo";
import { InventoryLogo } from "../../assests/logo/inventoryLogo";
import { TncLogo } from "../../assests/logo/tncLogo";
import { LogOutLogo } from "../../assests/logo/logOutLogo";
import { UserManagementLogo } from "../../assests/logo/userManagementLogo";
import DashboardIcon from "@mui/icons-material/Dashboard";
import { useLocation, useNavigate } from "react-router-dom";
import React, { useEffect, useMemo, useRef } from "react";
import { Typography } from "@mui/material";
import { PaymentResolutionLogo } from "../../assests/logo/PayoutResolution";
import { ShipDirectLogo } from "../../assests/logo/ShipDirect";
import { Layers } from "lucide-react";
import { WalletLogo } from "../../assests/logo/walletLogo";
import { GiftCardsLogo } from "../../assests/logo/GiftCardsLogo";
import { PayoutsLogo } from "../../assests/logo/Payouts";
import { ShippingAndTrackingLogo } from "../../assests/logo/ShippingAndTracking";
import { BankLogo } from "../../assests/logo/BankLogo";

export function useRouter() {
  const navigate = useNavigate();
  const location = useLocation();
  const user = getUsers();
  const ctrlClickRef = useRef(false); // Add ref to track ctrl click

  useEffect(() => {
    const handleCtrlClick = (event) => {
      if (event.ctrlKey || event.metaKey) {
        ctrlClickRef.current = true; // Set ref to true
      } else {
        ctrlClickRef.current = false; // Reset ref to false
      }
    };

    document.addEventListener("mousedown", handleCtrlClick); // Use mousedown

    return () => {
      document.removeEventListener("mousedown", handleCtrlClick);
    };
  }, []);

  const router = useMemo(() => {
    let isDiscord = isDiscordIsVerified();

    return {
      pathname: location.pathname,
      searchParams: new URLSearchParams(location.search),
      navigate: (path) => {
        sessionStorage.removeItem("searchQuery");
        sessionStorage.removeItem("tablePage");
        sessionStorage.removeItem("page");

        if (ctrlClickRef.current) {
          window.open(path, "_blank", "noopener,noreferrer");
          ctrlClickRef.current = false;
          return;
        }

        if (user?.role === "MEMBER" && !isDiscord && path !== "/") {
          navigate("/user-dashboard");
        } else if (user?.role === "MEMBER" && !isDiscord && path === "/") {
          localStorage.clear();
          sessionStorage.clear();
          const logoutChannel = new BroadcastChannel("logout_channel");
          logoutChannel.postMessage("logout");
          navigate(path);
        } else {
          navigate(path);
        }
      },
    };
  }, [location, navigate, user]);

  return router;
}

function useNavigation() {
  const navigate = useNavigate();
  const router = useRouter();
  const user = getUsers();
  const userRole = user?.role;

  const UNFILTERED_MANAGEMENT_NAVIGATION = [
    {
      permission: user?.permission_details?.dashboard,
      segment:
        userRole === "SUPERADMIN"
          ? "admin-dashboard"
          : userRole === "MANAGER" && "manager-dashboard",
      title: "Dashboard",
      icon: (
        <DashboardIcon
          style={{
            color:
              router.pathname.includes("/admin-dashboard") ||
              router.pathname.includes("/manager-dashboard")
                ? "#00838f"
                : "#ffffff60",
          }}
        />
      ),
    },
    {
      permission: user?.permission_details?.live_orders || user?.permission_details?.completed_orders,
      segment: "dashboard/orders",
      title: "Orders",
      icon: (
        <OpenOrdersLogo
          color={
            router.pathname.includes("/dashboard/orders") ||
            router.pathname.includes("/dashboard/live-orders") ||
            router.pathname.includes("/dashboard/completed-orders")
              ? "#00838f"
              : "#fff"
          }
        />
      ),
    },
    {
      permission: user?.permission_details?.pending_rsc_quotes,
      segment: "dashboard/pending-rsc-quotes",
      title: "Pending RSC Quotes",
      icon: (
        <QuotesLogo
          color={
            router.pathname.includes("/dashboard/pending-rsc-quotes")
              ? "#00838f"
              : "#fff"
          }
        />
      ),
    },
    {
      permission: user?.permission_details?.pre_quoted_cooks,
      segment: "dashboard/sell-catalog",
      title: "Sell Catalog",
      icon: (
        <PreQuotedLogo
          color={
            router.pathname.includes("/dashboard/sell-catalog") ||
            router.pathname.includes("/dashboard/prequoted-cooks")
              ? "#00838f"
              : "#fff"
          }
        />
      ),
    },
    {
      permission: user?.permission_details?.pre_quoted_cooks,
      segment: "dashboard/brands-and-sets",
      title: "Brands & Sets",
      icon: (
        <Layers
          size={20}
          color={
            router.pathname.includes("/dashboard/brands-and-sets")
              ? "#00838f"
              : "#fff"
          }
        />
      ),
    },
    {
      permission: user?.permission_details?.in_store_flips,
      segment: "dashboard/ship-direct",
      title: "Ship Direct",
      icon: (
        <ShipDirectLogo
          color={
            router.pathname.includes("/dashboard/ship-direct")
              ? "#00838f"
              : "#fff"
          }
        />
      ),
    },
    {
      permission: user?.permission_details?.in_store_flips,
      segment: "dashboard/gift-cards",
      title: "Gift Cards",
      icon: (
        <GiftCardsLogo
          color={
            router.pathname.includes("/dashboard/gift-cards")
              ? "#00838f"
              : "#fff"
          }
        />
      ),
    },
    {
      permission: user?.permission_details?.shipping_and_tracking,
      segment: "dashboard/shipment",
      title: "Shipping & Tracking",
      icon: (
        <ShippingLogo
          color={
            router.pathname.includes("/dashboard/shipment") ? "#00838f" : "#fff"
          }
        />
      ),
    },
    {
      permission: user?.permission_details?.shipping_and_tracking,
      segment: "dashboard/payouts",
      title: "Payouts",
      icon: (
        <PayoutLogo
          color={
            router.pathname.includes("/dashboard/payouts") ? "#00838f" : "#fff"
          }
        />
      ),
      navigable: true,
      children: [
        {
          permission: user?.permission_details?.payout_cases,
          title: "Wallet Credit",
          icon: (
            <WalletLogo
              color={
                router.pathname === "/dashboard/payouts" ? "#00838f" : "#fff"
              }
              height="20"
              width="20"
            />
          ),
        },
        {
          permission: user?.permission_details?.payout_cases, // Using same permission or add specific one
          segment: "withdraw-request",
          title: "Withdraw Requests",
          icon: (
            <BankLogo
              color={
                router.pathname === "/dashboard/payouts/withdraw-request"
                  ? "#00838f"
                  : "#fff"
              }
            />
          ),
        },
        {
          permission: user?.permission_details?.payout_cases, // Using same permission or add specific one
          segment: "payout-resolution",
          title: "Payment Resolution",
          icon: (
            <PaymentResolutionLogo
              color={
                router.pathname === "/dashboard/payouts/payout-resolution"
                  ? "#00838f"
                  : "#fff"
              }
            />
          ),
        },
      ],
    },
    {
      segment: "warehouse/inbound",
      title: "Inbound",
      icon: (
        <InboundLogo
          color={
            router.pathname.includes("/warehouse/inbound")
              ? "#00838f"
              : "#fff"
          }
        />
      ),
    },
    {
      segment: "warehouse/inventory",
      title: "Inventory",
      icon: (
        <InventoryLogo
          color={
            router.pathname.includes("/warehouse/inventory")
              ? "#00838f"
              : "#fff"
          }
        />
      ),
    },
    {
      permission: user?.permission_details?.user_management,
      segment: "dashboard/user-management",
      title: "User Management",
      icon: (
        <UserManagementLogo
          color={
            router.pathname.includes("/dashboard/user-management")
              ? "#00838f"
              : "#fff"
          }
        />
      ),
    },
    // {
    //   permission: user?.permission_details?.analytics,
    //   segment: "dashboard/analytics",
    //   title: "Analytics",
    //   icon: (
    //     <AnalyticsLogo
    //       color={
    //         router.pathname.includes("/dashboard/analytics")
    //           ? "#00838f"
    //           : "#fff"
    //       }
    //     />
    //   ),
    // },
    {
      permission: user?.permission_details?.terms_and_conditions,
      segment: "dashboard/t&c",
      title: "Terms & Conditions",
      icon: (
        <TncLogo
          color={
            router.pathname.includes("/dashboard/t&c") ? "#00838f" : "#fff"
          }
        />
      ),
    },
    {
      permission: user?.permission_details?.terms_and_conditions,
      segment: "dashboard/privacy-policy",
      title: "Privacy Policy",
      icon: (
        <TncLogo
          color={
            router.pathname.includes("/dashboard/privacy-policy") ? "#00838f" : "#fff"
          }
        />
      ),
    },
    {
      permission: true,
      icon: (
        <LogOutLogo
          color={router.pathname.includes("/logout") ? "#00838f" : "#fff"}
        />
      ),
      action: (
        <>
          <Typography
            width={"85%"}
            textAlign={"left"}
            onClick={() => {
              sessionStorage.clear();
              localStorage.clear();
              const logoutChannel = new BroadcastChannel("logout_channel");
              logoutChannel.postMessage("logout");
              navigate("/");
            }}
          >
            Logout
          </Typography>
        </>
      ),
    },
  ];

  const VENDOR_NAVIGATION = [
    {
      permission: user?.permission_details?.in_store_flips,
      segment: "dashboard/gift-cards",
      title: "Gift Cards",
      icon: (
        <GiftCardsLogo
          color={
            router.pathname.includes("/dashboard/gift-cards")
              ? "#00838f"
              : "#fff"
          }
        />
      ),
    },
    {
      permission: user?.permission_details?.shipping_and_tracking,
      segment: "dashboard/withdraw-request",
      title: "Withdraw Requests",
      icon: (
        <BankLogo
          color={
            router.pathname === "/dashboard/payouts/withdraw-request"
              ? "#00838f"
              : "#fff"
          }
        />
      ),
    },
    {
      permission: true,
      icon: (
        <LogOutLogo
          color={router.pathname.includes("/logout") ? "#00838f" : "#fff"}
        />
      ),
      action: (
        <>
          <Typography
            width={"85%"}
            textAlign={"left"}
            onClick={() => {
              sessionStorage.clear();
              localStorage.clear();
              const logoutChannel = new BroadcastChannel("logout_channel");
              logoutChannel.postMessage("logout");
              navigate("/");
            }}
          >
            Logout
          </Typography>
        </>
      ),
    },
  ];

  const MEMBER_NAVIGATION = [
    {
      segment: "user-dashboard",
      title: "Dashboard",
      icon: (
        <DashboardIcon
          style={{
            color: router.pathname.includes("/user-dashboard")
              ? "#00838f"
              : "#ffffff60",
          }}
        />
      ),
    },
    {
      segment: "dashboard/open-orders",
      title: "Open Orders",
      icon: (
        <OpenOrdersLogo
          color={
            router.pathname.includes("/dashboard/open-orders")
              ? "#00838f"
              : "#fff"
          }
        />
      ),
    },
    {
      segment: "dashboard/completed-orders",
      title: "Completed Orders",
      icon: (
        <OpenOrdersLogo
          color={
            router.pathname.includes("/dashboard/completed-orders")
              ? "#00838f"
              : "#fff"
          }
        />
      ),
    },
    {
      segment: "dashboard/quotes",
      title: "Quotes",
      icon: (
        <QuotesLogo
          color={
            router.pathname.includes("/dashboard/quotes") ? "#00838f" : "#fff"
          }
        />
      ),
    },
    {
      segment: "dashboard/sell",
      title: "Sell",
      icon: (
        <PreQuotedLogo
          color={
            router.pathname.includes("/dashboard/sell")
              ? "#00838f"
              : "#fff"
          }
        />
      ),
    },
    {
      segment: "dashboard/member-ship-direct",
      title: "Ship Direct",
      icon: (
        <ShipDirectLogo
          color={
            router.pathname.includes("/dashboard/member-ship-direct")
              ? "#00838f"
              : "#fff"
          }
        />
      ),
    },

    {
      segment: "dashboard/shipment",
      title: "Shipments",
      icon: (
        <ShippingLogo
          color={
            router.pathname.includes("/dashboard/shipment") ? "#00838f" : "#fff"
          }
        />
      ),
      navigable: true,
      children: [
        {
          permission: user?.permission_details?.payout_cases,
          title: "Shipping & Tracking",
          icon: (
            <ShippingAndTrackingLogo
              color={
                router.pathname === "/dashboard/shipment" ? "#00838f" : "#fff"
              }
            />
          ),
        },
        {
          permission: user?.permission_details?.payout_cases, // Using same permission or add specific one
          segment: "member-payouts",
          title: "Payouts",
          icon: (
            <PayoutsLogo
              color={
                router.pathname === "/dashboard/shipment/member-payouts"
                  ? "#00838f"
                  : "#fff"
              }
            />
          ),
        },
      ],
    },
    {
      segment: "dashboard/wallet",
      title: "Wallet & Gift Cards",
      icon: (
        <PayoutLogo
          color={
            router.pathname.includes("/dashboard/wallet") ? "#00838f" : "#fff"
          }
        />
      ),
      navigable: true,
      children: [
        {
          permission: user?.permission_details?.payout_cases,
          title: "Wallet",
          icon: (
            <WalletLogo
              color={
                router.pathname === "/dashboard/wallet" ? "#00838f" : "#fff"
              }
              width="20"
              height="20"
            />
          ),
        },
        {
          permission: user?.permission_details?.payout_cases, // Using same permission or add specific one
          segment: "gift-cards",
          title: "Gift Cards",
          icon: (
            <GiftCardsLogo
              color={
                router.pathname === "/dashboard/wallet/gift-cards"
                  ? "#00838f"
                  : "#fff"
              }
            />
          ),
        },
        {
          permission: user?.permission_details?.payout_cases, // Using same permission or add specific one
          segment: "payment-methods",
          title: "Payment Methods",
          icon: (
            <BankLogo
              color={
                router.pathname === "/dashboard/wallet/payment-methods"
                  ? "#00838f"
                  : "#fff"
              }
            />
          ),
        },
      ],
    },
    // {
    //   segment: "dashboard/member-payouts",
    //   title: "Payouts",
    //   icon: (
    //     <PayoutLogo
    //       color={
    //         router.pathname.includes("/dashboard/member-payouts")
    //           ? "#00838f"
    //           : "#fff"
    //       }
    //     />
    //   ),
    // },
    {
      segment: "dashboard/help-support",
      title: "Help & Support",
      icon: (
        <HelpLogo
          color={
            router.pathname.includes("/dashboard/help-support")
              ? "#00838f"
              : "#fff"
          }
        />
      ),
    },
    {
      segment: "dashboard/t&c",
      title: "Terms & Conditions",
      icon: (
        <TncLogo
          color={
            router.pathname.includes("/dashboard/t&c") ? "#00838f" : "#fff"
          }
        />
      ),
    },
    {
      segment: "dashboard/privacy-policy",
      title: "Privacy Policy",
      icon: (
        <TncLogo
          color={
            router.pathname.includes("/dashboard/privacy-policy") ? "#00838f" : "#fff"
          }
        />
      ),
    },
    {
      icon: (
        <LogOutLogo
          color={router.pathname.includes("/logout") ? "#00838f" : "#fff"}
        />
      ),
      action: (
        <>
          <Typography
            width={"85%"}
            textAlign={"left"}
            onClick={() => {
              sessionStorage.clear();
              localStorage.clear();
              navigate("/");
            }}
          >
            Logout
          </Typography>
        </>
      ),
    },
  ];

  const WAREHOUSE_NAVIGATION = [
    {
      segment: "warehouse/inbound",
      title: "Inbound",
      icon: (
        <InboundLogo
          color={
            router.pathname.includes("/warehouse/inbound")
              ? "#00838f"
              : "#fff"
          }
        />
      ),
    },
    {
      segment: "warehouse/inventory",
      title: "Inventory",
      icon: (
        <InventoryLogo
          color={
            router.pathname.includes("/warehouse/inventory")
              ? "#00838f"
              : "#fff"
          }
        />
      ),
    },
    {
      permission: user?.permission_details?.terms_and_conditions,
      segment: "dashboard/t&c",
      title: "Terms & Conditions",
      icon: (
        <TncLogo
          color={
            router.pathname.includes("/dashboard/t&c") ? "#00838f" : "#fff"
          }
        />
      ),
    },
    {
      permission: user?.permission_details?.terms_and_conditions,
      segment: "dashboard/privacy-policy",
      title: "Privacy Policy",
      icon: (
        <TncLogo
          color={
            router.pathname.includes("/dashboard/privacy-policy") ? "#00838f" : "#fff"
          }
        />
      ),
    },
    {
      permission: true,
      icon: (
        <LogOutLogo
          color={router.pathname.includes("/logout") ? "#00838f" : "#fff"}
        />
      ),
      action: (
        <>
          <Typography
            width={"85%"}
            textAlign={"left"}
            onClick={() => {
              sessionStorage.clear();
              localStorage.clear();
              const logoutChannel = new BroadcastChannel("logout_channel");
              logoutChannel.postMessage("logout");
              navigate("/");
            }}
          >
            Logout
          </Typography>
        </>
      ),
    },
  ];

  const MANAGEMENT_NAVIGATION = UNFILTERED_MANAGEMENT_NAVIGATION.filter(
    (item) => item.permission
  );

  return userRole === "MEMBER"
    ? MEMBER_NAVIGATION
    : userRole === "SUPERADMIN"
      ? UNFILTERED_MANAGEMENT_NAVIGATION
      : userRole === "VENDOR"
        ? VENDOR_NAVIGATION
        : userRole === "WAREHOUSE_ADMIN" || userRole === "WAREHOUSE_STAFF"
          ? WAREHOUSE_NAVIGATION
        : userRole === "MANAGER" && MANAGEMENT_NAVIGATION;
}

export default useNavigation;
